// Shared API helper — the single fetchJSON for the app; per-page copies
// drift into variants, so always import this one. docs/workflow-ui.md §kit.
//
// Behavior contract:
//  - always sends cookies (credentials: 'include');
//  - sets 'Content-Type: application/json' ONLY when the body is not FormData
//    (multipart uploads must let the browser set the boundary header);
//  - non-2xx → throws Error with .status and .code from the error envelope;
//  - a dead login session (codes in SESSION_ENDED_CODES) gets a plain-language
//    message instead of the raw server text. AuthContext watches every API
//    response for these codes and sends the user back to the login screen.

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Auth-middleware codes meaning the auth-token cookie is missing or no longer
// valid (expired after being idle, logged out in another tab, password
// changed, account disabled, sign-in address moved).
export const SESSION_ENDED_CODES = new Set([
  'NO_TOKEN', 'INVALID_TOKEN', 'SESSION_REVOKED', 'ACCOUNT_DISABLED', 'EMAIL_CHANGED',
]);

// EMAIL_CHANGED is the one session-ended code that carries something the
// generic message cannot say — which address to sign in with now — so its
// server text is shown verbatim on the login screen instead.
export const SESSION_ENDED_CODES_WITH_OWN_MESSAGE = new Set(['EMAIL_CHANGED']);

export const SESSION_ENDED_MESSAGE =
  'Your login session has ended — it expires after a period of inactivity, or when you log out or change your password elsewhere. ' +
  'Please log in again.';

// Fired on window after any successful write to a workflow route (below);
// the Layout listens and refetches the nav badge counts.
export const WORKFLOW_CHANGED_EVENT = 'workflow:changed';

export async function fetchJSON(url, opts = {}) {
  const isFormData = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  const res = await fetch(`${API_BASE_URL}${url}`, {
    credentials: 'include',
    ...opts,
    headers: {
      ...(opts.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = SESSION_ENDED_CODES.has(data.code) && !SESSION_ENDED_CODES_WITH_OWN_MESSAGE.has(data.code)
      ? SESSION_ENDED_MESSAGE
      : data.error || `Request failed (${res.status})`;
    const err = new Error(message);
    err.status = res.status;
    err.code = data.code;
    err.body = data; // full error envelope (e.g. IN_USE responses carry usedBy)
    throw err;
  }
  // A write to a workflow route (submit, approve, return, resubmit, delete)
  // changes what the nav badges count; the Layout refetches them on this
  // event instead of waiting for the next navigation.
  const method = String(opts.method || 'GET').toUpperCase();
  if (method !== 'GET' && /^\/api\/workflow\//.test(url) && typeof window !== 'undefined') {
    window.dispatchEvent(new Event(WORKFLOW_CHANGED_EVENT));
  }
  return data;
}
